import { PopoverPlacement } from "@idui/react-popover";
import { CalendarToday } from "@mui/icons-material";
import { Popover } from "@mui/material";
import { StaticDateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useDynamicFormsListActions,
  useEmitterListener,
  usePageContext,
} from "@ryde/redux";
import {
  formatDatetime,
  getComputedGridFieldName,
  getIsDisabledByCondition,
  getUTCDateOrNowInISO,
  GlobalAppEventEnum,
  ICustomField,
  IExtendableDto,
  isValidDateTime,
  profilePermissionsSelector,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { FramerTooltip, LocalizationProvider } from "../../common";
import { CustomMuiPickersActionBar } from "../../controls";
import { IRenderColumnTemplate } from "./_core";
import {
  DataEditableButtonContentStyled,
  DataEditableStyledButton,
  DateEditableTemplateWrapper,
} from "./date-editable.styled";

export const DateEditableTemplate: FC<IRenderColumnTemplate> = ({
  value: initialValue,
  gridAttributes,
  editFn,
  rowId,
  dateFormat,
  timeFormat,
  name,
  isColumn,
  rowOriginal,
}) => {
  const rowFieldKey = useMemo(
    () =>
      getComputedGridFieldName({
        name,
        isColumn,
        gridAttributes,
      } as ICustomField),
    [gridAttributes, isColumn, name],
  );

  const refValue = useRef(initialValue);
  const roleDataItem = useRecoilValue(profilePermissionsSelector);
  const [value, setValue] = useState<TDateISO | null>(initialValue);
  const [tempValue, setTempValue] = useState<TDateISO | null>(initialValue);
  const { timezone } = useTimezone();
  const { entity } = usePageContext();
  const { replaceTableRow } = useDynamicFormsListActions(entity);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  useEffect(() => {
    if (initialValue !== refValue.current) {
      refValue.current = initialValue;
      setValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (value !== tempValue && !open) {
      setTempValue(value);
    }
  }, [open, tempValue, value]);

  const formattedValue = formatDatetime({
    dateTimeISO: value,
    dateFormat,
    timeFormat,
    timezone,
  });
  const isDisabledByCondition = getIsDisabledByCondition({
    fieldValues: rowOriginal,
    conditions: gridAttributes?.disableCondition,
    permissions: roleDataItem?.permissions,
  });

  const isEditable = gridAttributes?.isEditable && !isDisabledByCondition;

  const { isAMPM } = useFormats();

  const setDate = useEvent(async (val: dayjs.Dayjs | null): Promise<void> => {
    setAnchorEl(null);

    if (editFn && rowId && val) {
      const isoValue = getUTCDateOrNowInISO(val, timezone);
      const editResult = await editFn(rowId, {
        [rowFieldKey]: isoValue,
      });

      if (editResult.success) {
        setValue(isoValue);
        replaceTableRow({
          ...(rowOriginal as IExtendableDto),
          [rowFieldKey]: isoValue,
        });
      }
    }
  });

  const handleClose = useEvent((): void => {
    setAnchorEl(null);
  });

  const handleClick = useEvent(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setTempValue(value);
      setAnchorEl(event.currentTarget);
    },
  );

  useEmitterListener({
    event: GlobalAppEventEnum.START_DRAG,
    handler: handleClose,
    isEnabled: open,
  });

  return isEditable ? (
    <LocalizationProvider>
      <DataEditableStyledButton onClick={handleClick}>
        <FramerTooltip
          content={formattedValue}
          placement={PopoverPlacement.bottom}
        >
          <DataEditableButtonContentStyled>
            {formattedValue}
            <CalendarToday
              sx={{
                width: "15px",
                height: "15px",
                marginTop: "-2px",
                cursor: "pointer",
              }}
            />
          </DataEditableButtonContentStyled>
        </FramerTooltip>
      </DataEditableStyledButton>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <StaticDateTimePicker
          value={
            tempValue && isValidDateTime(tempValue)
              ? dayjs.utc(tempValue)
              : null
          }
          onChange={newValue => {
            if (newValue && isValidDateTime(newValue)) {
              setTempValue(getUTCDateOrNowInISO(newValue, timezone));
            }
          }}
          onAccept={setDate}
          ampm={isAMPM}
          timezone={timezone}
          slots={{
            actionBar: props => (
              <CustomMuiPickersActionBar
                customOnCancel={handleClose}
                customOnAccept={handleClose}
                {...props}
              />
            ),
          }}
        />
      </Popover>
    </LocalizationProvider>
  ) : (
    <DateEditableTemplateWrapper data-e2e-template="datetime-cell">
      {isValidDateTime(value) ? formattedValue : ""}
    </DateEditableTemplateWrapper>
  );
};
